import { useAuth } from "@vellum/micro-app-auth";
import { Button, ConfirmDialog, describeVisibility } from "@vellum/ui";
import { UserRoles, type FeatureFlagSchema } from "@vellum/vellum-api";
import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

import { vellumApiSdk } from "@vellum/micro-app-auth";
import {
  FeatureFlagFormDialog,
  type FeatureFlagFormValues,
} from "@/components/FeatureFlagFormDialog";
// The configured SDK, not @vellum/vellum-api directly — importing this module
// is what points the client at the API origin with credentials.

function targetingSummary(flag: FeatureFlagSchema): string {
  if (flag.everyone) {
    return "Everyone";
  }

  const parts: string[] = [];
  if (flag.roles && flag.roles.length > 0) {
    parts.push(`Roles: ${describeVisibility(flag.roles)}`);
  }
  if (flag.user_ids && flag.user_ids.length > 0) {
    const count = flag.user_ids.length;
    parts.push(`${count} user${count === 1 ? "" : "s"}`);
  }

  return parts.length > 0 ? parts.join(" · ") : "No one";
}

export function FeatureFlagsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === UserRoles.ADMIN;

  const [flags, setFlags] = useState<FeatureFlagSchema[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [formTarget, setFormTarget] = useState<{
    flag: FeatureFlagSchema | null;
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<FeatureFlagSchema | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);
  const [writeFailed, setWriteFailed] = useState(false);

  const loadFlags = useCallback(async () => {
    const result = await vellumApiSdk.listFeatureFlags();
    setFlags(result.data ?? []);
    setFailed(result.data === undefined);
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      return;
    }

    let active = true;
    void (async () => {
      try {
        await loadFlags();
      } catch {
        if (active) {
          setFailed(true);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [isAdmin, loadFlags]);

  // The generated SDK resolves with an `error` instead of throwing, so a failed
  // write is a value to check, not an exception to catch.
  const failedWrite = (result: { error?: unknown }) =>
    result.error !== undefined;

  const submitForm = async (values: FeatureFlagFormValues) => {
    const body = {
      key: values.key.trim(),
      description: values.description.trim() || null,
      enabled: values.enabled,
      everyone: values.everyone,
      roles: values.roles,
      user_ids: values.user_ids,
    };

    try {
      const result = formTarget?.flag
        ? await vellumApiSdk.updateFeatureFlag({
            body,
            path: { flag_id: formTarget.flag.id },
          })
        : await vellumApiSdk.createFeatureFlag({ body });

      if (failedWrite(result)) {
        setWriteFailed(true);
        return;
      }

      await loadFlags();
      setWriteFailed(false);
      setFormTarget(null);
    } catch {
      setWriteFailed(true);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }

    setDeleting(true);
    try {
      const result = await vellumApiSdk.deleteFeatureFlag({
        path: { flag_id: pendingDelete.id },
      });
      if (failedWrite(result)) {
        setWriteFailed(true);
        return;
      }

      await loadFlags();
      setWriteFailed(false);
      setPendingDelete(null);
    } catch {
      setWriteFailed(true);
    } finally {
      setDeleting(false);
    }
  };

  // The API also rejects non-admins; the redirect just keeps them off the page.
  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      <div className="mb-2 flex items-start justify-between gap-4">
        <h1 className="text-3xl font-bold tracking-tight text-green-700">
          Feature flags
        </h1>
        {!loading && !failed ? (
          <Button onClick={() => setFormTarget({ flag: null })}>
            Add flag
          </Button>
        ) : null}
      </div>
      <p className="mb-7 max-w-xl text-sm leading-relaxed text-gray-600">
        Gate Luna UI behind server-evaluated flags. Toggle a flag on, then
        target everyone, specific roles, or individual users.
      </p>

      {writeFailed ? (
        <p className="mb-4 rounded-md bg-red-50 p-3 text-sm text-error">
          That change did not save. Check your connection and try again.
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500">Loading feature flags…</p>
      ) : failed ? (
        <p className="text-sm text-gray-500">
          We could not load feature flags just now. Refresh the page to try
          again.
        </p>
      ) : flags.length === 0 ? (
        <p className="text-sm text-gray-500">
          No feature flags yet. Add one to get started.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <th className="py-2 pr-4">Key</th>
                <th className="py-2 pr-4">Description</th>
                <th className="py-2 pr-4">Status</th>
                <th className="py-2 pr-4">Targeting</th>
                <th className="py-2 pr-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {flags.map((flag) => (
                <tr
                  key={flag.id}
                  className="border-b border-gray-100 align-top"
                >
                  <td className="py-3 pr-4 font-mono text-xs text-gray-800">
                    {flag.key}
                  </td>
                  <td className="py-3 pr-4 text-gray-600">
                    {flag.description || "—"}
                  </td>
                  <td className="py-3 pr-4">
                    <span
                      className={
                        flag.enabled
                          ? "inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700"
                          : "inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500"
                      }
                    >
                      {flag.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </td>
                  <td className="py-3 pr-4 text-gray-600">
                    {targetingSummary(flag)}
                  </td>
                  <td className="py-3 pr-4">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setFormTarget({ flag })}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        className="border-red-300 text-error hover:bg-red-50"
                        onClick={() => setPendingDelete(flag)}
                      >
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {formTarget ? (
        <FeatureFlagFormDialog
          isOpen
          flag={formTarget.flag}
          onClose={() => setFormTarget(null)}
          onSubmit={submitForm}
        />
      ) : null}

      <ConfirmDialog
        variant="delete"
        isOpen={pendingDelete !== null}
        loading={deleting}
        title={`Delete ${pendingDelete?.key ?? "this flag"}?`}
        onClose={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      >
        <p className="mt-2 text-sm text-gray-600">
          The flag will stop being evaluated for everyone. This cannot be undone
          from here.
        </p>
      </ConfirmDialog>
    </div>
  );
}
